import React, { useEffect, useState } from "react";
import EditModalAdmin from "./EditModalAdmin";
import UserLogo from "../../assets/logoAvatar.svg";
import { useMutation, useQuery } from "@apollo/client";
import {
  ADD_ADMIN,
  GET_ALL_ADMINS,
  REMOVE_ADMIN,
  UPDATE_USER_BY_ID,
} from "../../api/graphql/users";
import BasicButton from "../../BasicButton";
import { Dropdown } from "antd";
import { CloseOutlined, MoreOutlined } from "@ant-design/icons";
import Page from "../Page";
import { SearchInput } from "../Filters";
import DataCards, { CardColumn } from "../DataTable/DataCards";
import useIsPhone from "../../hooks/useIsPhone";
import styles from "./styles.module.scss";

// The client's sort arrows: both in quaternary, the active one darker.
const SortArrows: React.FC<{ direction?: "asc" | "desc" }> = ({
  direction,
}) => (
  <svg
    className={styles.sortIcon}
    viewBox="0 0 16 24"
    fill="currentColor"
    aria-hidden
  >
    <path
      d="M8 6L12 11H4L8 6Z"
      className={direction === "asc" ? styles.sortActive : undefined}
    />
    <path
      d="M8 18L4 13H12L8 18Z"
      className={direction === "desc" ? styles.sortActive : undefined}
    />
  </svg>
);

interface Admin {
  id: string;
  __typename: string;
  name: string;
  email: string;
  position: string;
  avatarUrl: string;
  role: "ADMIN" | "CLIENT";
}

const AdminPanel: React.FC = () => {
  const { data: adminsList, loading, refetch } = useQuery(GET_ALL_ADMINS);
  const [admins, setAdmins] = useState<Admin[]>([]);

  const [searchTerm, setSearchTerm] = useState("");
  const isPhone = useIsPhone();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddAdminModalOpen, setAddAdminModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);
  const [emailError, setEmailError] = useState(false);
  const [email, setEmail] = useState("");
  const [modalAction, setModalAction] = useState<"edit" | "remove" | null>(
    null,
  );
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);
  const [updateAdmin] = useMutation(UPDATE_USER_BY_ID);
  const [addAdmin] = useMutation(ADD_ADMIN);
  const [removeAdmin] = useMutation(REMOVE_ADMIN);

  useEffect(() => {
    if (adminsList?.getAllAdmins) {
      setAdmins(adminsList?.getAllAdmins);
    }
  }, [adminsList]);

  const handleOpenModal = (admin: Admin) => {
    setSelectedAdmin(admin);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAdmin(null);
    setModalAction(null);
  };

  const handleSelectAction = async (action: "edit" | "remove") => {
    setModalAction(action);
    setIsModalOpen(false);

    if (action === "edit") {
      setIsEditModalOpen(true);
    } else {
      await handleRemoveAdmin();
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedAdmin(null);
  };

  const handleEditAdmin = async (
    updatedAdmin: Partial<Admin>,
  ): Promise<void> => {
    setAdmins(
      // adminsList?.getAllAdmins.map((admin: Record<string, any>) =>
      adminsList?.getAllAdmins?.map((admin: Record<string, any>) =>
        admin.id === updatedAdmin.id ? updatedAdmin : admin,
      ),
    );
    console.log(updatedAdmin);
    const newUser = { ...updatedAdmin };
    delete newUser?.id;
    delete newUser?.__typename;

    await updateAdmin({
      variables: {
        userId: updatedAdmin.id,
        updateUser: newUser,
      },
    }).then(() => refetch());
    handleCloseEditModal();
  };

  const handleRemoveAdmin = async () => {
    if (selectedAdmin) {
      setAdmins(
        adminsList?.getAllAdmins?.filter(
          (admin: Record<string, any>) => admin.id !== selectedAdmin.id,
        ),
      );
      console.log(selectedAdmin);
    }
    await removeAdmin({
      variables: {
        email: selectedAdmin?.email,
      },
    });
    handleCloseModal();
  };

  const handleDeleteAdmin = () => {
    if (selectedAdmin) {
      setAdmins(
        adminsList?.getAllAdmins?.filter(
          (admin: Record<string, any>) => admin.id !== selectedAdmin.id,
        ),
      );
    }
    handleCloseEditModal();
  };

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredAdmins = admins.filter((admin: Record<string, any>) => {
    const name = (admin.name || "").toLowerCase();
    const email = (admin.email || "").toLowerCase();
    const position = (admin.position || "").toLowerCase();

    return (
      name.includes(normalizedSearchTerm) ||
      email.includes(normalizedSearchTerm) ||
      position.includes(normalizedSearchTerm)
    );
  });

  const sortedAdmins = React.useMemo(() => {
    if (!sortConfig) return filteredAdmins;
    const getComparableValue = (item: Admin, key: keyof Admin) =>
      (item?.[key]?.toString() || "").toLowerCase();
    const sorted = [...filteredAdmins].sort((a, b) => {
      const aValue = getComparableValue(a, sortConfig.key as keyof Admin);
      const bValue = getComparableValue(b, sortConfig.key as keyof Admin);

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredAdmins, sortConfig]);

  const handleSort = (key: keyof Admin) => {
    let direction: "asc" | "desc" = "asc";
    if (
      sortConfig &&
      sortConfig.key === key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // The row menu, in the table and on a phone's cards.
  const renderActions = (admin: Record<string, any>) => (
    // A portal menu, so the table's scroll box can't clip it.
    <Dropdown
      trigger={["click"]}
      placement="bottomRight"
      onOpenChange={(open) => open && handleOpenModal(admin as Admin)}
      menu={{
        items: [
          {
            key: "edit",
            label: "Edit",
            onClick: async () => await handleSelectAction("edit"),
          },
          {
            key: "remove",
            label: "Remove admin",
            onClick: async () => await handleSelectAction("remove"),
          },
        ],
      }}
    >
      <button className={styles.dotsButton} aria-label="Actions">
        <MoreOutlined />
      </button>
    </Dropdown>
  );

  const renderName = (admin: Record<string, any>) => (
    <div className={styles.nameCell}>
      <img src={admin.avatarUrl || UserLogo} alt="" className={styles.avatar} />
      {admin.name}
    </div>
  );

  // A phone: a card per admin, the name heading it and the menu beside it.
  const cardColumns: CardColumn<Record<string, any>>[] = [
    { key: "name", title: "Name", mobile: "title", render: renderName },
    { key: "actions", title: "", mobile: "header", render: renderActions },
    {
      key: "email",
      title: "Email",
      mobile: "full",
      render: (admin) => admin.email,
    },
    {
      key: "position",
      title: "Job Position",
      mobile: "full",
      render: (admin) => admin.position,
    },
  ];

  if (loading) return <Page title="Superadmin panel">Loading...</Page>;

  return (
    <Page
      title="Superadmin panel"
      actions={
        <BasicButton
          onClick={() => setAddAdminModal(true)}
          title="Add admin"
          variant="primary"
        />
      }
    >
      <SearchInput
        placeholder="Search user"
        value={searchTerm}
        onChange={setSearchTerm}
      />
      {isPhone ? (
        <DataCards
          columns={cardColumns}
          rows={sortedAdmins}
          rowKey={(admin) => admin.id}
          emptyText="No admins"
        />
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th onClick={() => handleSort("name")}>
                  <span className={styles.headCell}>
                    Name
                    <SortArrows
                      direction={
                        sortConfig?.key === "name"
                          ? sortConfig.direction
                          : undefined
                      }
                    />
                  </span>
                </th>
                <th onClick={() => handleSort("email")}>
                  <span className={styles.headCell}>
                    Email
                    <SortArrows
                      direction={
                        sortConfig?.key === "email"
                          ? sortConfig.direction
                          : undefined
                      }
                    />
                  </span>
                </th>
                <th onClick={() => handleSort("position")}>
                  <span className={styles.headCell}>
                    Job Position
                    <SortArrows
                      direction={
                        sortConfig?.key === "position"
                          ? sortConfig.direction
                          : undefined
                      }
                    />
                  </span>
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {sortedAdmins.map((admin: Record<string, any>) => (
                <tr key={admin.id}>
                  <td>{renderName(admin)}</td>
                  <td>{admin.email}</td>
                  <td>{admin.position}</td>
                  <td className={styles.actionsCell}>{renderActions(admin)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {isAddAdminModalOpen && (
        <div className={styles.overlay}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Add user</h3>
              <button
                type="button"
                onClick={() => setAddAdminModal(false)}
                className={styles.close}
                aria-label="Close"
              >
                <CloseOutlined />
              </button>
            </div>
            <form
              className={styles.modalForm}
              onSubmit={async () => {
                if (!emailError) {
                  await addAdmin({
                    variables: {
                      email,
                    },
                  });
                }
              }}
            >
              <input
                type="email"
                name="email"
                placeholder="Email"
                onChange={({ target: { value } }) => {
                  const regex = /^[a-zA-Z0-9._%+-]+@promobile\.[a-zA-Z]{2,}$/;
                  setEmailError(regex.test(value));
                  setEmail(value);
                }}
                value={email}
                className={`${styles.input} ${emailError ? styles.inputError : ""}`}
              />
              <div className={styles.modalFooter}>
                <BasicButton
                  onClick={() => setAddAdminModal(false)}
                  title="Cancel"
                  variant="secondary"
                  sx={{ flex: 1 }}
                />
                <BasicButton
                  type="submit"
                  title="Add"
                  variant="primary"
                  sx={{ flex: 1 }}
                />
              </div>
            </form>
          </div>
        </div>
      )}
      {isEditModalOpen && selectedAdmin && (
        <EditModalAdmin
          admin={selectedAdmin}
          onClose={handleCloseEditModal}
          onSave={handleEditAdmin}
          onDelete={handleDeleteAdmin}
        />
      )}
    </Page>
  );
};

export default AdminPanel;
